import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';

interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * 208 (#308, research.md R3): optional — when given, renders a back control (mirroring the
   * app's own `PageHeader.tsx` visual language) resolved via the shared `useBackNavigation` hook.
   * Omitted, this renders exactly as it always has — no back control, no behavior change for the
   * 37 pages that already use this component without one.
   */
  fallback?: string;
}

/**
 * Its own component, not an inline conditional hook call: `useBackNavigation` calls
 * `useNavigate()`, which throws outside a `<Router>` — the 37 existing `PageHeader` call sites
 * with no `fallback` must not suddenly require one just because this feature exists. Rendering
 * this child ONLY when `fallback` is given (a normal conditional-render, not a conditional hook
 * call) keeps `useNavigate()` uncalled for every page that doesn't opt in.
 */
function BackButton({ fallback }: { fallback: string }) {
  const goBack = useBackNavigation(fallback);
  return (
    <button
      type="button"
      aria-label="Back"
      onClick={goBack}
      className="mt-0.5 shrink-0 text-steel hover:text-ink"
    >
      <ChevronLeft size={20} aria-hidden="true" />
    </button>
  );
}

export function PageHeader({ title, description, actions, fallback }: PageHeaderProps) {
  return (
    <div className="sticky -top-16 lg:-top-8 z-10 -mx-8 -mt-16 px-8 pt-16 pb-4 mb-6 lg:-mt-8 lg:pt-8 bg-canvas border-b border-hairline flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        {fallback !== undefined && <BackButton fallback={fallback} />}
        <div>
          <h1 className="text-2xl font-bold text-ink">{title}</h1>
          {description && <p className="mt-1 text-sm text-steel">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}
